import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { addWorkout } from '../services/firestore';

const AddWorkout = ({ onWorkoutAdded }) => {
    const { user } = useAuth();
    const { darkMode } = useTheme();
    const [exercises, setExercises] = useState([{ name: '', reps: '', sets: '' }]);
    const [workoutDate, setWorkoutDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
    const [saving, setSaving] = useState(false);

    const addExercise = () => {
        setExercises([...exercises, { name: '', reps: '', sets: '' }]);
    };

    const updateExercise = (index, field, value) => {
        const updated = exercises.map((exercise, i) =>
            i === index ? { ...exercise, [field]: value } : exercise
        );
        setExercises(updated);
    };

    const removeExercise = (index) => {
        setExercises(exercises.filter((_, i) => i !== index));
    };

    const handleSaveWorkout = async () => {
        if (!user) {
            toast.error('Please sign in to save workouts');
            return;
        }

        // Validate exercises
        const validExercises = exercises.filter(
            exercise => exercise.name.trim() && exercise.sets && exercise.reps
        );

        if (validExercises.length === 0) {
            toast.warning('Please add at least one complete exercise');
            return;
        }

        // Validate date (can't be in the future)
        const selectedDate = new Date(workoutDate);
        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today

        if (selectedDate > today) {
            toast.warning('Cannot log workouts for future dates');
            return;
        }

        console.log('Saving workout with exercises:', validExercises);
        console.log('Workout date:', workoutDate);
        setSaving(true);

        try {
            await addWorkout(user.uid, {
                exercises: validExercises,
                date: workoutDate // Pass the selected date
            });

            // Reset form
            setExercises([{ name: '', reps: '', sets: '' }]);
            setWorkoutDate(new Date().toISOString().split('T')[0]); // Reset to today

            const workoutDateFormatted = new Date(workoutDate).toLocaleDateString();
            toast.success(`Workout saved for ${workoutDateFormatted}! 🎉 You earned ${validExercises.length} point${validExercises.length > 1 ? 's' : ''}!`);

            // Notify parent component to refresh data
            if (onWorkoutAdded) onWorkoutAdded();
        } catch (error) {
            console.error('Error saving workout:', error);
            console.error('Error details:', error.code, error.message);
            toast.error(`Error saving workout: ${error.message || 'Please try again.'}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto px-4 py-8">
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-6`}>Add New Workout</h2>

            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
                <div className="space-y-6">
                    {/* Date Selection */}
                    <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} pb-4`}>
                        <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                            Workout Date
                        </label>
                        <input
                            type="date"
                            value={workoutDate}
                            onChange={(e) => setWorkoutDate(e.target.value)}
                            max={new Date().toISOString().split('T')[0]} // Prevent future dates
                            className={`w-full px-3 py-2 border ${darkMode
                                    ? 'border-gray-600 bg-gray-700 text-white focus:ring-blue-400'
                                    : 'border-gray-300 bg-white text-gray-900 focus:ring-blue-500'
                                } rounded-md focus:outline-none focus:ring-2`}
                        />
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                            {workoutDate === new Date().toISOString().split('T')[0]
                                ? "Today's workout"
                                : `Workout for ${new Date(workoutDate).toLocaleDateString()}`
                            }
                        </p>
                    </div>

                    {/* Exercises */}
                    {exercises.map((exercise, index) => (
                        <div key={index} className={`border ${darkMode ? 'border-gray-600' : 'border-gray-200'} rounded-lg p-4`}>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Exercise {index + 1}</h3>
                                {exercises.length > 1 && (
                                    <button
                                        onClick={() => removeExercise(index)}
                                        className="text-red-600 hover:text-red-800"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                                        Exercise Name
                                    </label>
                                    <input
                                        type="text"
                                        value={exercise.name}
                                        onChange={(e) => updateExercise(index, 'name', e.target.value)}
                                        className={`w-full px-3 py-2 border ${darkMode
                                                ? 'border-gray-600 bg-gray-700 text-white focus:ring-blue-400'
                                                : 'border-gray-300 bg-white text-gray-900 focus:ring-blue-500'
                                            } rounded-md focus:outline-none focus:ring-2`}
                                        placeholder="e.g., Push-ups"
                                    />
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                                        Sets
                                    </label>
                                    <input
                                        type="number"
                                        value={exercise.sets}
                                        onChange={(e) => updateExercise(index, 'sets', e.target.value)}
                                        className={`w-full px-3 py-2 border ${darkMode
                                                ? 'border-gray-600 bg-gray-700 text-white focus:ring-blue-400'
                                                : 'border-gray-300 bg-white text-gray-900 focus:ring-blue-500'
                                            } rounded-md focus:outline-none focus:ring-2`}
                                        placeholder="3"
                                        min="1"
                                    />
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                                        Reps
                                    </label>
                                    <input
                                        type="number"
                                        value={exercise.reps}
                                        onChange={(e) => updateExercise(index, 'reps', e.target.value)}
                                        className={`w-full px-3 py-2 border ${darkMode
                                                ? 'border-gray-600 bg-gray-700 text-white focus:ring-blue-400'
                                                : 'border-gray-300 bg-white text-gray-900 focus:ring-blue-500'
                                            } rounded-md focus:outline-none focus:ring-2`}
                                        placeholder="10"
                                        min="1"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                    <button
                        onClick={addExercise}
                        className={`w-full py-2 px-4 border-2 border-dashed ${darkMode
                                ? 'border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300'
                                : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700'
                            } rounded-lg transition duration-200`}
                    >
                        + Add Another Exercise
                    </button>

                    <div className="flex gap-4">
                        <button
                            onClick={handleSaveWorkout}
                            disabled={saving}
                            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : 'Save Workout'}
                        </button>
                        <button
                            onClick={() => {
                                setExercises([{ name: '', reps: '', sets: '' }]);
                                setWorkoutDate(new Date().toISOString().split('T')[0]);
                            }}
                            className={`px-4 py-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}
                        >
                            Reset
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddWorkout;